"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./DualRangeSlider.module.scss";
import { DualRangeSliderProps, SliderValues } from "./DualRangeSlider.types";

const DualRangeSlider = ({
  minValue,
  maxValue,
  min,
  max,
  step,
  onChange,
  ariaLabelMin = "Minimum value",
  ariaLabelMax = "Maximum value",
  className,
  disabled = false,
}: DualRangeSliderProps) => {
  const [minVal, setMinVal] = useState(minValue);
  const [maxVal, setMaxVal] = useState(maxValue);
  const minValRef = useRef(minValue);
  const maxValRef = useRef(maxValue);
  const progressRef = useRef<HTMLDivElement>(null);

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  const updateProgressBar = useCallback(() => {
    if (!progressRef.current) return;

    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxVal);

    progressRef.current.style.left = `${minPercent}%`;
    progressRef.current.style.width = `${maxPercent - minPercent}%`;
  }, [minVal, maxVal, getPercent]);

  const handleMinChange = useCallback(
    (value: number) => {
      if (disabled) return;
      
      const newValue = Math.min(value, maxVal - step);
      setMinVal(newValue);
      minValRef.current = newValue;
      
      const newValues: SliderValues = { min: newValue, max: maxVal };
      onChange(newValues);
    },
    [maxVal, step, onChange, disabled]
  );

  const handleMaxChange = useCallback(
    (value: number) => {
      if (disabled) return;
      
      const newValue = Math.max(value, minVal + step);
      setMaxVal(newValue);
      maxValRef.current = newValue;
      
      const newValues: SliderValues = { min: minVal, max: newValue };
      onChange(newValues);
    },
    [minVal, step, onChange, disabled]
  );

  useEffect(() => {
    if (minValue !== minVal) {
      setMinVal(minValue);
      minValRef.current = minValue;
    }
  }, [minValue, minVal]);

  useEffect(() => {
    if (maxValue !== maxVal) {
      setMaxVal(maxValue);
      maxValRef.current = maxValue;
    }
  }, [maxValue, maxVal]);

  useEffect(() => {
    updateProgressBar();
  }, [updateProgressBar]);

  const wrapperClassName = `${styles.sliderWrapper} ${className || ''}`.trim();

  return (
    <div className={wrapperClassName}>
      <div className={styles.container}>
        <div className={styles.sliderTrack} ref={progressRef} />
        <div className={styles.sliderTrackBackground} />
        <input
          type="range"
          min={min}
          max={max}
          value={minVal}
          step={step}
          disabled={disabled}
          className={`${styles.slider} ${styles.minRange}`}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          aria-label={ariaLabelMin}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={maxVal}
          step={step}
          disabled={disabled}
          className={`${styles.slider} ${styles.maxRange}`}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          aria-label={ariaLabelMax}
        />
      </div>
    </div>
  );
};

export default DualRangeSlider; 